// client/src/pages/Home/Dashboard.jsx
// Accueil refondu style manga : hero + CTA duel + grille de tuiles.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAudio } from '../../hooks/useAudio';
import api from '../../api/api';
import Icon from '../../components/ui/Icon';
import { QuestPanel, SeasonPanel } from '../../components/dashboard/HomePanels';
import './Dashboard.css';
import cockpitVideo from '../../assets/videos/Cockpit.mp4';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const {
    playMusic,
    pauseMusic,
    startDashboardMusic,
    resumeAudioContext,
    adjustVolume,
    isPlaying,
    currentMusicSource,
    checkAndRestartAudio,
    hasError
  } = useAudio();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [showNews, setShowNews] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [musicStarted, setMusicStarted] = useState(false);

  const [newsItems, setNewsItems] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userStats, setUserStats] = useState({
    wins: 0,
    losses: 0,
    cardsCollected: 0,
    decks: 0,
    rank: 'Novice'
  });
  const [activeGames, setActiveGames] = useState(0);

  const startBackgroundMusic = useCallback(() => {
    resumeAudioContext();
    adjustVolume(0.3);
    setTimeout(() => {
      startDashboardMusic();
      setMusicStarted(true);
    }, 300);
  }, [resumeAudioContext, adjustVolume, startDashboardMusic]);

  useEffect(() => {
    if (musicStarted) {
      const audioCheckInterval = setInterval(() => {
        if (!isPlaying) checkAndRestartAudio();
      }, 2000);
      return () => clearInterval(audioCheckInterval);
    }
  }, [musicStarted, isPlaying, checkAndRestartAudio]);

  useEffect(() => {
    if (!isPlaying && musicStarted) {
      setTimeout(() => checkAndRestartAudio(), 500);
    }
  }, [musicStarted, isPlaying, checkAndRestartAudio]);

  useEffect(() => {
    const loadData = async () => {
      if (!localStorage.getItem('elementum_onboarding_done')) {
        setShowOnboarding(true);
      }

      const today = new Date();
      const iso = (d) => d.toISOString().slice(0, 10);
      setNewsItems([
        {
          id: 1,
          title: 'La saison des éléments est ouverte',
          content: "Plus de 500 cartes, 6 éléments, des duels classés contre d'autres duellistes. Le classement vous attend.",
          date: iso(today),
          type: 'announcement'
        },
        {
          id: 2,
          title: '505 cartes à collectionner',
          content: 'Communes, rares, épiques et légendaires : ouvrez des packs à la boutique et complétez votre collection.',
          date: iso(new Date(today.getTime() - 7 * 864e5)),
          type: 'event'
        }
      ]);

      try {
        const [statsRes, gamesRes] = await Promise.all([
          api.get('/stats').catch(() => null),
          api.get('/games').catch(() => null),
        ]);

        if (statsRes?.data?.stats) {
          setUserStats({
            wins: statsRes.data.stats.wins || 0,
            losses: statsRes.data.stats.losses || 0,
            cardsCollected: statsRes.data.stats.cardsCollected || 0,
            decks: statsRes.data.stats.decks || 0,
            rank: getRankFromStats(statsRes.data.stats),
          });
        }

        if (gamesRes?.data?.games) {
          setActiveGames(gamesRes.data.games.length);
        }
      } catch (err) {
        console.warn('[Dashboard] Could not load stats:', err.message);
      }
    };

    loadData();
  }, []);

  const getRankFromStats = (stats) => {
    const wins = stats.wins || 0;
    if (wins >= 50) return 'Maître Légendaire';
    if (wins >= 30) return 'Maître';
    if (wins >= 15) return 'Duelliste Expérimenté';
    if (wins >= 5) return 'Duelliste';
    if (wins >= 1) return 'Aventurier';
    return 'Novice';
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.volume = 0.7;
      videoRef.current.play().catch(() => {});
      if (!musicStarted || currentMusicSource !== 'dashboard') {
        setTimeout(() => startBackgroundMusic(), 800);
      }
    }
  }, [musicStarted, startBackgroundMusic, currentMusicSource]);

  const navigateTo = (path) => navigate(path);

  const dismissOnboarding = () => {
    localStorage.setItem('elementum_onboarding_done', '1');
    setShowOnboarding(false);
  };

  const closeAllPanels = () => {
    setShowNews(false);
    setShowStats(false);
    setShowStatus(false);
  };

  const togglePanel = (panel) => {
    closeAllPanels();
    if (panel === 'news') setShowNews(!showNews);
    if (panel === 'stats') setShowStats(!showStats);
    if (panel === 'status') setShowStatus(!showStatus);
  };

  const totalGames = userStats.wins + userStats.losses;
  const winRate = totalGames > 0 ? Math.round((userStats.wins / totalGames) * 100) : null;

  const TILES = [
    { key: 'campaign', label: 'Campagne', icon: 'sparkles', to: '/campaign', accent: 'a-orange' },
    { key: 'duel', label: 'DUEL', icon: 'play', to: '/game/matchmaking', accent: 'a-green', tour: 'nav-play' },
    { key: 'collection', label: 'Collection', icon: 'collection', to: '/collection', accent: 'a-cyan', badge: userStats.cardsCollected, tour: 'nav-collection' },
    { key: 'shop', label: 'Boutique', icon: 'shop', to: '/shop', accent: 'a-gold', tour: 'nav-shop' },
    { key: 'marketplace', label: 'Marketplace', icon: 'shop', to: '/marketplace', accent: 'a-gold', tour: 'nav-marketplace' },
    { key: 'rankings', label: 'Classement', icon: 'rankings', to: '/rankings', accent: 'a-violet', tour: 'nav-rankings' },
    { key: 'profile', label: 'Profil', icon: 'profile', to: '/profile', accent: 'a-blue' },
  ];

  return (
    <div className="dashboard-container">
      <video
        ref={videoRef}
        className="cockpit-video"
        autoPlay
        loop
        playsInline
        src={cockpitVideo}
        onEnded={() => {
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {});
          }
        }}
      />
      <div className="dashboard-overlay" />

      {/* Lignes de vitesse decoratives */}
      <div className="manga-speedlines" aria-hidden="true" />

      <div className="dash-ui">
        {/* Barre superieure : duelliste a gauche, panneaux a droite */}
        <header className="dash-topbar">
          <button
            className="dash-duelist"
            style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
            onClick={() => navigateTo(currentUser?.isGuest ? '/auth/login' : '/profile')}
            title={currentUser?.isGuest ? 'Invité — connecte-toi pour garder ta progression' : 'Voir mon profil'}
            data-tour="nav-profile"
          >
            <div className="dash-avatar">
              {(currentUser?.avatar?.url || currentUser?.companion?.avatarUrl) ? (
                <img className="dash-avatar-img" src={currentUser.avatar?.url || currentUser.companion.avatarUrl} alt="Avatar" />
              ) : (
                <Icon name="profile" size={20} />
              )}
            </div>
            <div className="dash-id">
              <span className="dash-username">{currentUser?.username || 'Duelliste'}</span>
              <span className="dash-rank">
                <Icon name="sparkles" size={11} /> {userStats.rank}
              </span>
            </div>
          </button>

          <div className="dash-panelbtns">
            <button className={`dash-pbtn ${showNews ? 'on' : ''}`} onClick={() => togglePanel('news')} title="Actualités">
              <Icon name="news" size={17} />
            </button>
            <button className={`dash-pbtn ${showStats ? 'on' : ''}`} onClick={() => togglePanel('stats')} title="Statistiques">
              <Icon name="stats" size={17} />
            </button>
            <button className={`dash-pbtn ${showStatus ? 'on' : ''}`} onClick={() => togglePanel('status')} title="État du jeu">
              <Icon name="status" size={17} />
            </button>
            {musicStarted && (
              <button
                className={`dash-pbtn music ${isPlaying ? 'on' : ''}`}
                onClick={() => (isPlaying ? pauseMusic() : startBackgroundMusic())}
                title={isPlaying ? 'Couper la musique' : 'Activer la musique'}
                aria-label={isPlaying ? 'Couper la musique' : 'Activer la musique'}
              >
                <Icon name={isPlaying ? 'pause' : 'music'} size={17} />
              </button>
            )}
          </div>
        </header>

        <main className="dash-main">
          {/* Onboarding premiere visite */}
          {showOnboarding && (
            <div className="onboarding-banner">
              <Icon name="sparkles" size={22} className="onboarding-icon" />
              <div className="onboarding-text">
                <h3>{currentUser?.isGuest ? 'Bienvenue, duelliste !' : `Bienvenue, ${currentUser?.username || 'duelliste'} !`}</h3>
                <p>
                  {currentUser?.isGuest
                    ? 'Vous jouez en invité : créez un compte pour conserver vos cartes, decks et victoires.'
                    : "Construisez votre deck, collectionnez les 505 cartes et affrontez d'autres duellistes."}
                </p>
                <div className="onboarding-actions">
                  <button className="dash-cta small" onClick={() => navigateTo('/game/matchmaking')}>
                    <Icon name="play" size={14} /> Lancer un duel
                  </button>
                  {currentUser?.isGuest && (
                    <button className="onboarding-link" onClick={() => navigateTo('/auth/register')}>
                      <Icon name="userplus" size={14} /> Créer un compte
                    </button>
                  )}
                  <button className="onboarding-dismiss" onClick={dismissOnboarding}>
                    Ne plus afficher
                  </button>
                </div>
              </div>
              <button className="onboarding-close" onClick={dismissOnboarding} aria-label="Fermer">
                <Icon name="close" size={16} />
              </button>
            </div>
          )}

          {/* Hero */}
          <section className="dash-hero">
            <p className="dash-kicker">Elementum</p>
            <h1 className="dash-title">NOVARCANE</h1>
            <p className="dash-tagline">Le duel des six éléments commence ici.</p>

            <div className="dash-quickstats">
              <div className="qs">
                <span className="qs-val">{userStats.wins}<em>V</em></span>
                <span className="qs-lbl">Victoires</span>
              </div>
              <div className="qs-sep" />
              <div className="qs">
                <span className="qs-val">{userStats.losses}<em>D</em></span>
                <span className="qs-lbl">Défaites</span>
              </div>
              <div className="qs-sep" />
              <div className="qs">
                <span className="qs-val">{winRate === null ? '—' : winRate + '%'}</span>
                <span className="qs-lbl">Ratio</span>
              </div>
            </div>

            <button className="dash-cta" onClick={() => navigateTo('/game/matchmaking')}>
              <Icon name="play" size={20} />
              <span>LANCER LE DUEL</span>
            </button>
          </section>

          {/* Grille de tuiles */}
          <nav className="dash-tiles" aria-label="Navigation rapide">
            {TILES.map((t) => (
              <button key={t.key} className={`dash-tile ${t.accent}`} onClick={() => navigateTo(t.to)} data-tour={t.tour}>
                <span className="dash-tile-icon"><Icon name={t.icon} size={22} /></span>
                <span className="dash-tile-label">{t.label}</span>
                {t.badge !== undefined && t.badge > 0 && (
                  <span className="dash-tile-badge">{t.badge}</span>
                )}
                <span className="dash-tile-slash" aria-hidden="true" />
              </button>
            ))}
          </nav>

          {/* R34f : quêtes quotidiennes + passe + saison */}
          <div className="dash-panels-grid">
            <QuestPanel />
            <SeasonPanel />
          </div>
        </main>
      </div>

      {/* Panneau actualités */}
      {showNews && (
        <div className="info-panel news-panel">
          <div className="panel-header">
            <h2>Actualités</h2>
            <button className="close-button" onClick={() => setShowNews(false)}><Icon name="close" size={18} /></button>
          </div>
          <div className="panel-content">
            {newsItems.map(item => (
              <div key={item.id} className={`news-item ${item.type}`}>
                <div className="news-header">
                  <h3>{item.title}</h3>
                  <span className="news-date">{item.date}</span>
                </div>
                <p>{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Panneau statistiques */}
      {showStats && (
        <div className="info-panel stats-panel">
          <div className="panel-header">
            <h2>Statistiques</h2>
            <button className="close-button" onClick={() => setShowStats(false)}><Icon name="close" size={18} /></button>
          </div>
          <div className="panel-content">
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{userStats.wins}</span>
                <span className="stat-label">Victoires</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{userStats.losses}</span>
                <span className="stat-label">Défaites</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{userStats.cardsCollected}</span>
                <span className="stat-label">Cartes</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{userStats.decks}</span>
                <span className="stat-label">Decks</span>
              </div>
            </div>
            <div className="win-ratio">
              <span className="ratio-label">Ratio de victoires :</span>
              <span className="ratio-value">
                {totalGames > 0 ? ((userStats.wins / totalGames) * 100).toFixed(1) + '%' : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Panneau statut du jeu */}
      {showStatus && (
        <div className="info-panel status-panel">
          <div className="panel-header">
            <h2>État du jeu</h2>
            <button className="close-button" onClick={() => setShowStatus(false)}><Icon name="close" size={18} /></button>
          </div>
          <div className="panel-content">
            <div className="status-info">
              <div className="status-item">
                <span className="status-label">Joueurs en ligne :</span>
                <span className="status-value">{activeGames > 0 ? activeGames + 1 : 1}</span>
              </div>
              <div className="status-item">
                <span className="status-label">Parties en cours :</span>
                <span className="status-value">{activeGames}</span>
              </div>
              <div className="status-item">
                <span className="status-label">Cartes disponibles :</span>
                <span className="status-value">505</span>
              </div>
              <div className="status-item audio-status">
                <span className="status-label">Audio :</span>
                <span className={`status-value ${isPlaying ? 'active' : 'inactive'}`}>
                  {isPlaying ? 'Actif' : 'Inactif'}
                  {!isPlaying && (
                    <button className="audio-restart-btn" onClick={startBackgroundMusic}>
                      Redémarrer
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
